<script lang="ts">
	import { PUBLIC_BASE_URL } from '$env/static/public';
	import { GtmStore } from '$lib/utils/gtm-store';
	import { StructureUtils } from '$lib/utils/structured-utils';
	import type { PageData } from './$types';
	import type { INewsFields } from '$lib/utils/contentful/types';
	import { page } from '$app/stores';
	// Components
	import { Head } from '$lib/components/atoms/Head';
	import { Breadcrumb, BreadcrumbItem } from '$lib/components/molecules/Breadcrumb';
	import { HeroStandard } from '$lib/components/organisms/Hero';
	import { getURLFromContentfulAsset } from '$lib/utils/assets-utils';
	import { dateFullMonthFormat } from '$lib/utils/formatter';
	import {
		CardContainer,
		PowertagComponent,
		Section,
		SectionTight
	} from '$lib/components/organisms/Section';
	import { ShareButton } from '$lib/components/atoms/Button';
	import { Prose } from '$lib/components/atoms/Prose';
	import { ArticleCard } from '$lib/components/organisms/Card';
	import { browser } from '$app/environment';
	import { RequestInfoForm } from '$lib/components/organisms/Form';

	export let data: PageData;

	export let news: INewsFields | null = data.news;
	export let recentNewsBuffer: INewsFields[] = [];
	const slug: string = $page.params.slug;

	let recentNews: INewsFields[] = data.recentNews;
	let ldScriptTag: string = '';

	let powertags: any[] = data.powertags;

	const preview: boolean = false;

	let seoDetails = { title: 'News' };

	const breadcrumbJson = StructureUtils.getBreadcrumbStructure(
		[
			{
				name: 'Home',
				url: PUBLIC_BASE_URL
			},
			{
				name: 'News',
				url: `${PUBLIC_BASE_URL}news`
			},

			{
				name: news.fields.title,
				url: `${PUBLIC_BASE_URL}news/${news.fields.slug}`
			}
		],
		true
	);

	const newsJson = StructureUtils.getArticleStructure(news, true);
	ldScriptTag = StructureUtils.createScriptTag(JSON.stringify([breadcrumbJson, newsJson]));

	/******************** GTM EVENT PUSH *************************/
	GtmStore.pushEvent({
		pageCategory: 'News',
		label: news?.fields?.title ? news.fields.title : ''
	});
	/*************************************************************/

	$: seoDetails = {
		title: news?.fields.title ? news.fields.title : '',
		description: news?.fields.intro ? news.fields.intro : '',
		url: `/news/${slug ? slug : ''}`,
		ldScriptTag: ldScriptTag
	};
</script>

<Head appendCompany {seoDetails} />

<Breadcrumb disableLastElementLink>
	<BreadcrumbItem>
		<a href="/">Home</a>
	</BreadcrumbItem>
	<BreadcrumbItem>
		<a href="/news">News</a>
	</BreadcrumbItem>
	<BreadcrumbItem current>
		<a href="/news/{slug}">{news.fields.title}</a>
	</BreadcrumbItem>
</Breadcrumb>

<HeroStandard
	darkOverlay
	data={{
		image: getURLFromContentfulAsset(news.fields.heroImage, preview),
		srcset: getURLFromContentfulAsset(news.fields.heroImage, preview),
		imageAlt:
			news.fields.heroImage && news.fields.heroImage.fields.description
				? news.fields.heroImage.fields.description
				: '',
		subtitle: dateFullMonthFormat(news.fields.publishedDate),
		title: news.fields.title
	}}
/>

<SectionTight compact variant="transparent" class="bg-gray-50 pb-6 md:pb-14">
	<div class="lg:text-md mx-auto py-7">
		<ShareButton title={news.fields.title} text={news.fields.intro} allowNative hideOnMobile />
	</div>

	<Prose fullWidth>
		<h2 slot="abstract" class="intro">
			{news.fields.intro}
		</h2>
	</Prose>

	<PowertagComponent prose {powertags} />
	<br /><br />

	{#if news.fields.newsVisibilities.includes('Abilita la news su Forma24') && news.fields.author && news.fields.author.fields.publicName && news.fields.author.fields.publicProfileLink}
		<div class="mt-4">
			<Prose fullWidth>
				Autore: <a href={news.fields.author.fields.publicProfileLink} target="_blank" rel="nofollow"
					>{news.fields.author.fields.publicName}</a
				>
			</Prose>
		</div>
	{/if}
</SectionTight>

{#if news && news.fields.showContactCTA}
	<SectionTight variant="transparent" class="bg-gray-50 py-6 md:py-14">
		<RequestInfoForm requestInfoType="news" {news} />
	</SectionTight>
{/if}

<Section compact class={news.fields.showContactCTA ? 'bg-gray-50' : 'bg-white'}>
	<svelte:fragment slot="header">
		<p class="subtitle mb-2">Leggi di più</p>
		<h3>Altre news che potrebbero interessarti</h3>
	</svelte:fragment>

	<CardContainer>
		{#each recentNews as article, index}
			<ArticleCard lazyLoad={true} {article} />
		{/each}
	</CardContainer>
</Section>

<style type="text/scss" lang="scss" scoped>
	.intro {
		@apply font-serif font-medium;
		@apply text-md md:text-2xl;
		@apply text-gray-900;
	}
</style>
